import { type FC } from "react";

import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconGoogle } from "@/assets/icons/geist/IconGoogle";
import { IconKey } from "@/assets/icons/geist/IconKey";
import { IconGoogleColor } from "@/assets/icons/IconGoogleColor";
import { Button } from "@/components/flexkit/Button";
import { ExternalLink } from "@/components/flexkit/typography/ExternalLink";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useFlagGitHubAppOnboardingEnabled } from "@/hooks/use-flag";
import type { PlainLoginProvider } from "@/queries/account-queries";
import { appendReturnTo } from "@/utils/login-url";

type Props = {
    onContinueWithSSO: () => void;
    returnTo: string;
    loginProviders?: PlainLoginProvider[];
    allowCustom?: boolean;
    hideLoginToClassic?: boolean;
    newLoginLayout?: boolean;
};
export const DefaultSSOForm: FC<Props & PropsWithClassName> = ({
    onContinueWithSSO,
    className,
    returnTo,
    hideLoginToClassic,
    newLoginLayout,
    loginProviders,
    allowCustom,
}) => {
    const showGithub = loginProviders?.some((p) => p.provider === "github") ?? false;
    const showGoogle = loginProviders?.some((p) => p.provider === "google") ?? false;
    const { value: gitHubAppOnboardingEnabled } = useFlagGitHubAppOnboardingEnabled();
    const customSSO = loginProviders?.find((p) => p.provider === "custom");
    const showContinueWithSSO = allowCustom === true;
    const showClassicAlternative = allowCustom === true && !hideLoginToClassic;

    const buildReturnUrl = ({ issuer, relative }: { issuer: string; relative?: boolean }) => {
        const url = new URL(returnTo, window.location.origin);
        url.searchParams.set("issuer", issuer);
        if (relative) {
            return encodeURIComponent(url.pathname + url.search);
        }
        return encodeURIComponent(url.toString());
    };

    return (
        <div id="default-sso-form" className={cn(className)}>
            {showGithub && (
                <Button
                    size="lg"
                    variant={newLoginLayout ? "primary" : "secondary"}
                    LeadingIcon={IconGitHub}
                    className="w-full"
                    asChild
                >
                    <a
                        id="signin-github"
                        href={
                            gitHubAppOnboardingEnabled
                                ? `/integrations/app/github/login?returnTo=${buildReturnUrl({ issuer: "github.com", relative: true })}`
                                : `/auth/oidc/start?returnTo=${buildReturnUrl({ issuer: "github.com" })}&issuer=github.com&reuseGithubToken=true`
                        }
                        data-tracking-id="signin-github"
                    >
                        <span className={cn({ "flex-grow text-center": !newLoginLayout })}>Connect with GitHub</span>
                    </a>
                </Button>
            )}
            {(showGoogle || showContinueWithSSO) && showGithub ? (
                <hr className="relative h-8 translate-y-1/2 overflow-visible border-border-base text-center after:absolute after:-translate-x-1/2 after:-translate-y-1/2 after:bg-surface-primary after:px-4 after:text-base after:leading-tight after:text-content-primary after:content-['Or']" />
            ) : null}
            {showGoogle && (
                <Button size="lg" variant={newLoginLayout ? "outline" : "secondary"} className="w-full" asChild>
                    <a
                        id="signin"
                        href={`/auth/oidc/start?returnTo=${encodeURIComponent(returnTo)}&issuer=https://accounts.google.com`}
                        data-tracking-id="signin-google"
                    >
                        {newLoginLayout ? <IconGoogleColor className="size-6" /> : <IconGoogle size="lg" />}
                        <span className={cn({ "flex-grow text-center": !newLoginLayout })}>Continue with Google</span>
                    </a>
                </Button>
            )}
            {showContinueWithSSO && (
                <Button
                    size="lg"
                    variant={newLoginLayout ? "outline" : "secondary"}
                    LeadingIcon={IconKey}
                    className="w-full"
                    onClick={onContinueWithSSO}
                    data-tracking-id="confirm-default-sso-form"
                >
                    <span className={cn({ "text-center": !newLoginLayout })}>Continue with SSO</span>
                </Button>
            )}
            {customSSO && (
                <a
                    id="signin-custom"
                    href={appendReturnTo(customSSO.loginUrl, returnTo)}
                    data-tracking-id="signin-custom-sso"
                >
                    <Button size="lg" variant="secondary" LeadingIcon={IconKey} className="w-full">
                        <span className={cn({ "flex-grow text-center": !newLoginLayout })}>Continue with SSO</span>
                    </Button>
                </a>
            )}

            {showClassicAlternative && (
                <Button size="lg" variant="link" className="w-full" asChild>
                    <ExternalLink href="https://gitpod.io/login">Log in to Gitpod Classic</ExternalLink>
                </Button>
            )}
        </div>
    );
};
